import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ListTemplate, ListItem, TemplateCategory } from '../types/lists';
import { templatesService } from '../services/templatesService';

interface TemplatesStoreState {
  // State
  templates: ListTemplate[];
  isLoading: boolean;
  error: string | null;

  // Actions
  initializeTemplates: () => void;

  // Template CRUD
  createTemplate: (data: { title: string; description: string; category: string; color?: string; estimatedHours?: number }) => Promise<void>;
  updateTemplate: (templateId: string, updates: Partial<ListTemplate>) => Promise<void>;
  deleteTemplate: (templateId: string) => Promise<void>;
  loadTemplates: () => void;

  // Utility methods
  getTemplateById: (id: string) => ListTemplate | undefined;
  getTemplatesByCategory: (category: TemplateCategory) => ListTemplate[];

  // Template item operations
  addTemplateItem: (templateId: string, content: string, itemData?: Partial<Omit<ListItem, 'id' | 'createdAt' | 'priority'>>) => Promise<void>;
  updateTemplateItem: (templateId: string, itemIndex: number, updates: Partial<Omit<ListItem, 'id' | 'createdAt' | 'priority'>>) => Promise<void>;
  deleteTemplateItem: (templateId: string, itemIndex: number) => Promise<void>;
  reorderTemplateItems: (templateId: string, reorderedItems: Array<Omit<ListItem, 'id' | 'createdAt' | 'priority'>>) => Promise<void>;

  // Error handling
  clearError: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
}

export const useTemplatesStore = create<TemplatesStoreState>()(
  devtools(
    (set, get) => ({
      // Initial state
      templates: [],
      isLoading: false,
      error: null,

      // Initialize templates
      initializeTemplates: () => {
        set({ isLoading: true, error: null });
        try {
          const templates = templatesService.getTemplates();

          console.log('📋 Templates Store initialized:', {
            templatesCount: templates.length,
          });

          set({
            templates,
            isLoading: false
          });
        } catch (error) {
          console.error('Failed to initialize templates:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to initialize templates',
            isLoading: false
          });
        }
      },

      // Create a new template
      createTemplate: async (data) => {
        set({ isLoading: true, error: null });
        try {
          await templatesService.createTemplate(data);
          const updatedTemplates = templatesService.getTemplates();
          set({ templates: updatedTemplates, isLoading: false });
          console.log('✅ Template created successfully:', data.title);
        } catch (error) {
          console.error('Failed to create template:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to create template',
            isLoading: false
          });
          throw error;
        }
      },

      // Update a template
      updateTemplate: async (templateId, updates) => {
        set({ error: null });
        try {
          await templatesService.updateTemplate(templateId, updates);
          const updatedTemplates = templatesService.getTemplates();
          set({ templates: updatedTemplates });
          console.log('✅ Template updated successfully');
        } catch (error) {
          console.error('Failed to update template:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to update template' });
          throw error;
        }
      },

      // Delete a template
      deleteTemplate: async (templateId) => {
        set({ error: null });
        try {
          await templatesService.deleteTemplate(templateId);
          const updatedTemplates = templatesService.getTemplates();
          set({ templates: updatedTemplates });
          console.log('✅ Template deleted successfully');
        } catch (error) {
          console.error('Failed to delete template:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to delete template' });
          throw error;
        }
      },

      // Load templates
      loadTemplates: () => {
        try {
          const templates = templatesService.getTemplates();
          set({ templates });
        } catch (error) {
          console.error('Failed to load templates:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to load templates' });
        }
      },

      // Utility methods
      getTemplateById: (id) => {
        return get().templates.find(template => template.id === id);
      },

      getTemplatesByCategory: (category) => {
        return get().templates.filter(template => template.category === category);
      },

      // Template item operations
      addTemplateItem: async (templateId, content, itemData) => {
        set({ error: null });
        try {
          await templatesService.addTemplateItem(templateId, content, itemData);
          const updatedTemplates = templatesService.getTemplates();
          set({ templates: updatedTemplates });
          console.log('✅ Template item added successfully');
        } catch (error) {
          console.error('Failed to add template item:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to add template item' });
          throw error;
        }
      },

      updateTemplateItem: async (templateId, itemIndex, updates) => {
        set({ error: null });
        try {
          await templatesService.updateTemplateItem(templateId, itemIndex, updates);
          const updatedTemplates = templatesService.getTemplates();
          set({ templates: updatedTemplates });
          console.log('✅ Template item updated successfully');
        } catch (error) {
          console.error('Failed to update template item:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to update template item' });
          throw error;
        }
      },

      deleteTemplateItem: async (templateId, itemIndex) => {
        set({ error: null });
        try {
          await templatesService.deleteTemplateItem(templateId, itemIndex);
          const updatedTemplates = templatesService.getTemplates();
          set({ templates: updatedTemplates });
          console.log('✅ Template item deleted successfully');
        } catch (error) {
          console.error('Failed to delete template item:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to delete template item' });
          throw error;
        }
      },

      reorderTemplateItems: async (templateId, reorderedItems) => {
        set({ error: null });
        try {
          await templatesService.reorderTemplateItems(templateId, reorderedItems);
          const updatedTemplates = templatesService.getTemplates();
          set({ templates: updatedTemplates });
          console.log('✅ Template items reordered successfully');
        } catch (error) {
          console.error('Failed to reorder template items:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to reorder template items' });
          throw error;
        }
      },

      // Error handling
      clearError: () => set({ error: null }),

      setError: (error) => set({ error }),

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'templates-store'
    }
  )
);
